import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { productsAPI, marketplaceAPI } from '../services/api';
import { formatDateTime } from '../utils/dateFormat';
import '../styles/MyAnnonces.css';

// Correspondance onglet -> statut(s) envoyé(s) au backend
const TAB_STATUS = {
  all: '',
  active: 'active',
  reserved: 'reserved',
  sold: 'sold',
  inactive: 'pending,expired,draft'
};

const MyProducts = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [sales, setSales] = useState([]);
  const [counts, setCounts] = useState({ draft: 0, pending: 0, active: 0, reserved: 0, sold: 0, expired: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, page: 1, totalPages: 1 });
  const [activeTab, setActiveTab] = useState('all');
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [salesCollapsed, setSalesCollapsed] = useState(false);
  const PER_PAGE = 24;
  const navigate = useNavigate();

  const API_URL = process.env.REACT_APP_API_URL || '/api';

  const statusLabels = {
    'draft': t('status.draft'),
    'pending': t('status.pending'),
    'active': t('status.active'),
    'reserved': t('status.soldPending'),
    'sold': t('status.sold'),
    'expired': t('status.expired')
  };

  const statusColors = {
    'draft': '#6c757d',
    'pending': '#ffc107',
    'active': '#28a745',
    'reserved': '#ff9800',
    'sold': '#17a2b8',
    'expired': '#dc3545'
  };

  // Débounce de la recherche : on n'interroge le serveur que 400 ms après la dernière frappe
  useEffect(() => {
    const id = setTimeout(() => {
      setSearch(searchInput.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(id);
  }, [searchInput]);

  useEffect(() => {
    fetchData(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, activeTab, search]);

  const fetchData = async (targetPage = page) => {
    try {
      setLoading(true);
      const [productsRes, salesRes] = await Promise.all([
        productsAPI.getUserProducts(targetPage, PER_PAGE, TAB_STATUS[activeTab], search),
        marketplaceAPI.getSalesHistory().catch(() => ({ data: [] }))
      ]);
      // Réponse paginée : { products, counts, pagination }
      const data = productsRes.data;
      const list = Array.isArray(data) ? data : (data.products || []);
      setProducts(list);
      if (data.counts) setCounts(data.counts);
      if (data.pagination) setPagination(data.pagination);
      setSales(salesRes.data || []);

      // Si on a supprimé le dernier article d'une page, revenir à la précédente
      if (list.length === 0 && targetPage > 1) {
        setPage(targetPage - 1);
      }
    } catch (err) {
      setError(t('myProducts.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async (productId) => {
    try {
      await productsAPI.publish(productId);
      fetchData();
    } catch (err) {
      setError(t('myProducts.publishError'));
    }
  };

  const handleUnpublish = async (productId) => {
    try {
      await productsAPI.unpublish(productId);
      fetchData();
    } catch (err) {
      setError(t('myProducts.unpublishError'));
    }
  };

  const handleMarkAsSold = async (productId) => {
    if (window.confirm(t('confirms.markSold'))) {
      try {
        await productsAPI.markAsSold(productId);
        fetchData();
      } catch (err) {
        setError('Erreur');
      }
    }
  };

  const handleDelete = async (productId) => {
    if (window.confirm(t('confirms.deleteProduct'))) {
      try {
        await productsAPI.delete(productId);
        fetchData();
      } catch (err) {
        setError(t('myProducts.deleteError'));
      }
    }
  };

  const changeTab = (tab) => {
    if (tab === activeTab) return;
    setActiveTab(tab);
    setPage(1);
  };

  // Trouver la commande liée à un produit
  const getOrderForProduct = (productId) => {
    return sales.find(s => s.product_id === productId && s.status !== 'cancelled' && s.status !== 'refunded');
  };

  // Ventes actives (payées, en cours de traitement) et revenus cumulés
  const activeSales = sales.filter(s => ['paid', 'shipped', 'delivered'].includes(s.status));
  const totalEarnings = sales
    .filter(s => !['cancelled', 'refunded'].includes(s.status))
    .reduce((sum, s) => sum + (parseFloat(s.seller_earnings) || 0), 0);

  // Compteurs dérivés pour les onglets (respectent la recherche côté serveur)
  const derived = {
    all: counts.active + counts.reserved + counts.sold + counts.pending + counts.expired + counts.draft,
    active: counts.active,
    reserved: counts.reserved,
    sold: counts.sold,
    inactive: counts.pending + counts.expired + counts.draft
  };

  const tabs = [
    { key: 'all', label: t('myProducts.filterAll'), count: derived.all },
    { key: 'active', label: t('myProducts.filterOnline'), count: derived.active },
    { key: 'reserved', label: t('myProducts.filterSelling'), count: derived.reserved },
    { key: 'sold', label: t('myProducts.filterSold'), count: derived.sold },
    { key: 'inactive', label: t('myProducts.filterInactive'), count: derived.inactive }
  ];

  return (
    <div className="my-annonces-container">
      <div className="my-annonces-header">
        <h2>{t('myProducts.title')}</h2>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <button
            className="btn-secondary"
            onClick={() => navigate('/bulk-import')}
          >
            📑 {t('myProducts.addMultiple')}
          </button>
          <button
            className="btn-primary"
            onClick={() => navigate('/create-product')}
          >
            {t('myProducts.addNew')}
          </button>
        </div>
      </div>

      {/* Barre de statistiques */}
      <div className="mp-stats">
        <div className="mp-stat mp-stat--online">
          <span className="mp-stat-value">{derived.active}</span>
          <span className="mp-stat-label">{t('myProducts.statOnline')}</span>
        </div>
        <div className="mp-stat mp-stat--selling">
          <span className="mp-stat-value">{activeSales.length}</span>
          <span className="mp-stat-label">{t('myProducts.statSelling')}</span>
        </div>
        <div className="mp-stat mp-stat--sold">
          <span className="mp-stat-value">{derived.sold}</span>
          <span className="mp-stat-label">{t('myProducts.statSold')}</span>
        </div>
        <div className="mp-stat mp-stat--earnings">
          <span className="mp-stat-value">{totalEarnings.toFixed(2)} <small>CHF</small></span>
          <span className="mp-stat-label">{t('myProducts.statEarnings')}</span>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Section ventes en cours (repliable) */}
      {activeSales.length > 0 && (
        <div className="sales-notification-section">
          <div className="sales-section-head">
            <h3 className="sales-section-title">
              {t('myProducts.activeSales', { count: activeSales.length })}
            </h3>
            <button
              className="sales-toggle-btn"
              onClick={() => setSalesCollapsed(c => !c)}
            >
              {salesCollapsed ? t('myProducts.showSales') : t('myProducts.hideSales')}
              <span className={`sales-toggle-caret ${salesCollapsed ? 'is-collapsed' : ''}`}>▾</span>
            </button>
          </div>

          {!salesCollapsed && activeSales.map(sale => {
            let shippingAddress = null;
            if (sale.shipping_address) {
              try {
                shippingAddress = typeof sale.shipping_address === 'string'
                  ? JSON.parse(sale.shipping_address)
                  : sale.shipping_address;
              } catch (e) {}
            }

            const imageUrl = sale.product_image
              ? `${API_URL.replace('/api', '')}/uploads/thumbnails/${sale.product_image}`
              : 'https://via.placeholder.com/80x80?text=?';

            const saleStatusLabels = {
              'paid': t('status.soldToShip'),
              'shipped': t('status.shippedStatus'),
              'delivered': t('status.deliveredPending')
            };

            const saleStatusColors = {
              'paid': '#ff9800',
              'shipped': '#6f42c1',
              'delivered': '#17a2b8'
            };

            return (
              <div key={sale.id} className="sale-notification-card">
                <div className="sale-notification-header">
                  <img src={imageUrl} alt={sale.product_title} className="sale-notif-image" />
                  <div className="sale-notif-info">
                    <h4>{sale.product_title}</h4>
                    <p className="sale-notif-price">+{sale.seller_earnings?.toFixed(2)} CHF</p>
                    <span
                      className="sale-notif-status"
                      style={{ backgroundColor: saleStatusColors[sale.status] }}
                    >
                      {saleStatusLabels[sale.status]}
                    </span>
                  </div>
                </div>

                <div className="sale-notification-details">
                  <div className="sale-notif-row">
                    <span className="sale-notif-label">{t('myProducts.buyerLabel', { name: '' }).split(':')[0]} :</span>
                    <span className="sale-notif-value">{sale.buyer_name}</span>
                  </div>
                  <div className="sale-notif-row">
                    <span className="sale-notif-label">{t('myProducts.shippingMode')}</span>
                    <span className="sale-notif-value">
                      {sale.shipping_method === 'shipping' ? '\u{1F4E6} ' + t('myProducts.postalShipping') : '\u{1F4CD} ' + t('myProducts.handDelivery')}
                    </span>
                  </div>

                  {sale.shipping_method === 'shipping' && shippingAddress && shippingAddress.name && (
                    <div className="sale-notif-row">
                      <span className="sale-notif-label">{t('myProducts.addressLabel')}</span>
                      <span className="sale-notif-value">
                        {shippingAddress.name}<br />
                        {shippingAddress.street}<br />
                        {shippingAddress.postalCode} {shippingAddress.city}
                      </span>
                    </div>
                  )}

                  {sale.buyer_message && (
                    <div className="sale-notif-row sale-notif-message">
                      <span className="sale-notif-label">{'\u{1F4AC}'} {t('myProducts.messageLabel')}</span>
                      <span className="sale-notif-value">"{sale.buyer_message}"</span>
                    </div>
                  )}

                  <div className="sale-notif-row">
                    <span className="sale-notif-label">{t('myProducts.dateLabel')}</span>
                    <span className="sale-notif-value">
                      {formatDateTime(sale.paid_at || sale.created_at)}
                    </span>
                  </div>
                </div>

                <div className="sale-notif-actions">
                  <button
                    className="btn-contact-buyer"
                    onClick={() => navigate('/my-messages')}
                  >
                    {t('myProducts.contactBuyer')}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Barre de filtres : onglets + recherche */}
      <div className="mp-toolbar">
        <div className="mp-tabs">
          {tabs.map(tab => (
            <button
              key={tab.key}
              className={`mp-tab ${activeTab === tab.key ? 'is-active' : ''}`}
              onClick={() => changeTab(tab.key)}
            >
              {tab.label}
              <span className="mp-tab-count">{tab.count}</span>
            </button>
          ))}
        </div>
        <div className="mp-search">
          <span className="mp-search-icon">🔍</span>
          <input
            type="text"
            className="mp-search-input"
            placeholder={t('myProducts.searchPlaceholder')}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <button className="mp-search-clear" onClick={() => setSearchInput('')} aria-label="clear">×</button>
          )}
        </div>
      </div>

      {/* Liste des articles */}
      {loading ? (
        <div className="loading-container"><p>{t('common.loading')}</p></div>
      ) : products.length === 0 ? (
        <div className="no-annonces">
          <p>{search || activeTab !== 'all' ? t('myProducts.noMatch') : t('myProducts.noProducts')}</p>
          {!search && activeTab === 'all' && (
            <button
              className="btn-primary"
              onClick={() => navigate('/create-product')}
            >
              {t('myProducts.firstProduct')}
            </button>
          )}
        </div>
      ) : (
        <div className="annonces-list">
          {products.map(product => {
            const imageUrl = product.images && product.images.length > 0
              ? `${API_URL.replace('/api', '')}/uploads/thumbnails/${product.images[0]}`
              : 'https://via.placeholder.com/100x100?text=Pas+d\'image';

            const order = getOrderForProduct(product.id);

            return (
              <div key={product.id} className={`annonce-item ${order ? 'annonce-item-sold' : ''}`}>
                <img src={imageUrl} alt={product.title} className="annonce-thumb" loading="lazy" />

                <div className="annonce-info">
                  <h3>{product.title}</h3>
                  <p className="annonce-price">{parseFloat(product.price).toFixed(2)} CHF</p>
                  <p className="annonce-details">
                    {t('myProducts.sizeAndBrand', { size: product.size, brand: product.brand || t('product.noBrand') })}
                  </p>
                  <span
                    className="annonce-status"
                    style={{ backgroundColor: statusColors[product.status] }}
                  >
                    {statusLabels[product.status]}
                  </span>

                  {/* Info vente inline */}
                  {order && (
                    <div className="annonce-order-info">
                      <p>{t('myProducts.buyerLabel', { name: order.buyer_name })}</p>
                      <p>{order.shipping_method === 'shipping' ? '\u{1F4E6} Poste' : '\u{1F4CD} Main propre'}</p>
                    </div>
                  )}
                </div>

                <div className="annonce-actions">
                  {(product.status === 'pending' || product.status === 'expired') && (
                    <button
                      className="btn-publish"
                      onClick={() => handlePublish(product.id)}
                    >
                      {t('common.publish')}
                    </button>
                  )}
                  {product.status === 'active' && (
                    <>
                      <button
                        className="btn-sold"
                        onClick={() => handleMarkAsSold(product.id)}
                      >
                        {t('myProducts.markSold')}
                      </button>
                      <button
                        className="btn-unpublish"
                        onClick={() => handleUnpublish(product.id)}
                      >
                        {t('common.unpublish')}
                      </button>
                    </>
                  )}
                  <button
                    className="btn-edit"
                    onClick={() => navigate(`/edit-product/${product.id}`)}
                  >
                    {t('common.edit')}
                  </button>
                  {product.status !== 'reserved' && product.status !== 'sold' && (
                    <button
                      className="btn-delete"
                      onClick={() => handleDelete(product.id)}
                    >
                      {t('common.delete')}
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="pagination">
          <button
            className="pagination__btn"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1 || loading}
          >
            {t('pagination.previous', 'Précédent')}
          </button>
          <span className="pagination__info">
            {t('pagination.pageInfo', 'Page {{page}} / {{total}}', { page: pagination.page, total: pagination.totalPages })}
            {pagination.total ? ` — ${pagination.total} ${t('pagination.items', 'articles')}` : ''}
          </span>
          <button
            className="pagination__btn"
            onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
            disabled={page >= pagination.totalPages || loading}
          >
            {t('pagination.next', 'Suivant')}
          </button>
        </div>
      )}
    </div>
  );
};

export default MyProducts;
